import { PageHeader, useToast } from '@/components';
import { MyUdidsSection } from '@/features/apple-udids/MyUdidsSection';
import { useAuth } from '@/features/auth/AuthProvider';
import { DateTimeSettings } from '@/features/profile/DateTimeSettings';
import { MySlackSection } from '@/features/slack/MySlackSection';
import { useLinkSlack } from '@/features/slack/hooks';
import { MyEmailsSection } from '@/features/user-emails/MyEmailsSection';
import { useSearchParams } from '@solidjs/router';
import { Show, onMount } from 'solid-js';

const SLACK_OUTCOME: Record<string, { ok: boolean; text: string }> = {
  linked: { ok: true, text: 'Slack linked — check your DMs for a welcome message' },
  taken: { ok: false, text: 'That Slack account is already linked to another user' },
  wrong_workspace: { ok: false, text: "That Slack workspace isn't the one Hera posts to" },
  error: { ok: false, text: 'Slack linking failed — please try again' },
};

export default function PersonalSettingsPage() {
  const { session, refresh } = useAuth();
  const toast = useToast();
  const linkSlack = useLinkSlack();
  const [params, setParams] = useSearchParams<{
    slack?: string;
    slack_code?: string;
    slack_state?: string;
  }>();

  const showOutcome = (key: string) => {
    const outcome = SLACK_OUTCOME[key];
    if (!outcome) return;
    if (outcome.ok) {
      toast.success(outcome.text);
      void refresh();
    } else toast.error(outcome.text);
  };

  // UCH-805: the Slack callback bounces here with ?slack_code&slack_state
  // (completed as the signed-in user) or ?slack=error.
  onMount(() => {
    const { slack, slack_code: code, slack_state: state } = params;
    setParams(
      { slack: undefined, slack_code: undefined, slack_state: undefined },
      { replace: true },
    );
    if (code && state) {
      linkSlack.mutate(
        { code, state },
        { onSuccess: showOutcome, onError: () => showOutcome('error') },
      );
    } else if (slack) showOutcome(slack);
  });

  return (
    <section>
      <PageHeader title="Personal settings" />
      <div class="mx-auto max-w-6xl p-6">
        <Show when={session()} keyed>
          {(s) => (
            <div class="flex flex-col gap-6">
              <div class="flex flex-col gap-4 rounded-xl border border-neutral-100 bg-surface p-6 shadow-sm dark:border-neutral-800 dark:bg-surface-dark">
                <h2 class="font-medium text-neutral-900 text-sm dark:text-neutral-100">
                  Date & time
                </h2>
                <DateTimeSettings
                  dateFormat={s.dateFormat}
                  timeFormat={s.timeFormat}
                  timezone={s.timezone}
                />
              </div>
              <div class="flex flex-col gap-4 rounded-xl border border-neutral-100 bg-surface p-6 shadow-sm dark:border-neutral-800 dark:bg-surface-dark">
                <h2 class="font-medium text-neutral-900 text-sm dark:text-neutral-100">
                  Apple devices (UDID)
                </h2>
                <MyUdidsSection />
              </div>
              <div class="flex flex-col gap-4 rounded-xl border border-neutral-100 bg-surface p-6 shadow-sm dark:border-neutral-800 dark:bg-surface-dark">
                <h2 class="font-medium text-neutral-900 text-sm dark:text-neutral-100">
                  Other emails
                </h2>
                <MyEmailsSection />
              </div>
              <div class="flex flex-col gap-4 rounded-xl border border-neutral-100 bg-surface p-6 shadow-sm dark:border-neutral-800 dark:bg-surface-dark">
                <h2 class="font-medium text-neutral-900 text-sm dark:text-neutral-100">Slack</h2>
                <MySlackSection
                  slackUserId={s.slackUserId}
                  slackLinkedAt={s.slackLinkedAt}
                  slackOpenUrl={s.slackOpenUrl}
                />
              </div>
            </div>
          )}
        </Show>
      </div>
    </section>
  );
}
